import { FilterIcon } from "../../Icons";
import { Typo } from "../../Typo";
import { usePerformanceReportTranslations } from "../hooks/usePerformanceReportTranslations";
import { usePerformanceReportContext } from "../providers/PerformanceReportProvider";
import { MobileDatePicker } from "./MobileDatePicker";

type MobileControlsProps = {
  /** Opens the filter bottom sheet (owned as page-level state — the sheet's
   * `initial`/`selectOptions` need to be threaded from the page, matching
   * desktop's `PerformanceControls`/`onOpenFilter` precedent). */
  onOpenFilter: () => void;
};

/**
 * Mobile controls row: date range / filter (AC-2). Two controls only; there
 * is no separate "columns" button on mobile — the sort sheet in
 * `MobileSortExportRow` doubles as the row list's single visible-metric
 * picker. This matches the updated AC-2/AC-5 (edited 2026-07 after the
 * design-vs-AC conflict was ruled on). The filter badge is a numeric count
 * (design-spec §3 "active-count badge"; confirmed 2026-07).
 *
 * The date pill is a self-contained `MobileDatePicker` (owns its own open
 * state + presets), matching how desktop's `Datepicker` is self-contained in
 * `PerformanceControls` — only the filter sheet is hoisted to page-level
 * state, since (like the desktop filter modal) it needs data threaded in from
 * the page.
 *
 * Fill/color/size match the canvas's `.m-datepick`/`.m-filterbtn` exactly
 * (tonal date pill, solid brand-color filter square with an inverse-primary
 * badge, 40px height) — re-verified 2026-07-21 against `js/app.jsx`
 * `MobileView`. AC-9's ≥44px tap-target rule was previously applied here
 * instead of the canvas's literal 40px, but ACs shouldn't dictate specific
 * UI measurements — the canvas is the source of truth for exact sizing
 * (see root CLAUDE.md's Design source hierarchy), so this reverts to 40px.
 */
export const MobileControls = ({ onOpenFilter }: MobileControlsProps) => {
  const translations = usePerformanceReportTranslations();
  const { filterCount } = usePerformanceReportContext();

  return (
    <div className="flex items-center gap-2">
      <MobileDatePicker />

      <div className="relative">
        <button
          type="button"
          aria-label={translations.mobileFilterAriaLabel}
          onClick={onOpenFilter}
          className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-brand-default text-text-inverse-primary hover:brightness-95"
        >
          <FilterIcon
            className="h-4 w-4"
            aria-hidden="true"
            focusable="false"
          />
        </button>
        {filterCount > 0 && (
          <span
            data-testid="filter-count-badge"
            className="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-surface-inverse-primary px-1.5"
          >
            <Typo
              element="span"
              type="label2XS"
              textColor="text-text-inverse-primary"
            >
              {filterCount}
            </Typo>
          </span>
        )}
      </div>
    </div>
  );
};
